import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Grid2,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  OutlinedInput,
  Tooltip,
  IconButton,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Search from "../components/Search";
import RadiusAPI from "../api/RadiusAPI";
import RadiusTable from "../components/RadiusTable";
import RefreshTwoToneIcon from "@mui/icons-material/RefreshTwoTone";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(filter, filters, theme) {
  return {
    fontWeight: filters.includes(filter)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

export default function Dashboard({ token }) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [radiusData, setRadiusData] = useState(null);
  const [selectList, setSelectList] = useState([]);
  const [collection, setCollection] = useState("");
  const [filters, setFilters] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [refreshLogs, setRefreshLogs] = useState(false);
  const [rawRadiusData, setRawRadiusData] = useState(null);
  const [searchDB, setSearchDB] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const defaultFilters = useMemo(
    () => [
      "username",
      "acctstarttime",
      "acctstoptime",
      "acctsessiontime",
      "acctinputoctets",
      "acctoutputoctets",
      "framedipaddress",
      "reply",
      "authdate",
    ],
    []
  );

  const handleRefresh = () => {
    nextSearchTime.current = 0;
    setRefreshLogs(!refreshLogs);
  }

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleCheckboxChange = (event) => {
    setSearchDB(event.target.checked);
  };

  const handleCollectionChange = (event) => {
    setFilters([]);
    setCollection(event.target.value);
  };

  const handleFilterChange = (event) => {
    const {
      target: { value },
    } = event;
    setFilters(typeof value === "string" ? value.split(",") : value);
  };

  const isObjectEmpty = (objectName) => {
    if (objectName) {
      return Object.keys(objectName).length !== 0;
    } else {
      return false;
    }
  };

  function secondsToDhms(seconds) {
    seconds = Number(seconds);
    var d = Math.floor(seconds / 86400);
    var h = Math.floor((seconds % 86400) / 3600);
    var m = Math.floor((seconds % 3600) / 60);
    var s = Math.floor(seconds % 60);

    var dDisplay = d > 0 ? d + (d === 1 ? "d " : "d ") : "";
    var hDisplay = h > 0 ? h + (h === 1 ? "h:" : "h:") : "";
    var mDisplay = m > 0 ? m + (m === 1 ? "m:" : "m:") : "";
    var sDisplay = s > 0 ? s + (s === 1 ? "s" : "s") : "";
    return dDisplay + hDisplay + mDisplay + sDisplay;
  }

  useEffect(() => {
    if (isObjectEmpty(radiusData)) {
      var filters_array = Object.keys(radiusData?.[0]);
      var final_filter_array = [];
      filters_array.forEach((value) => {
        !defaultFilters.includes(value) && final_filter_array.push(value);
      });
      var new_filters = [];
      filters.forEach((value) => {
        if (value.indexOf(final_filter_array)) {
          new_filters.push(value);
        }
      });
      setFilters(new_filters);
      setFilterOptions(final_filter_array);
    }
  }, [radiusData, collection, selectList]);

  const FormatRadiusData = useCallback(
    (raw_radius_data, current_collection, search_value) => {
      var updated_radius_data = [];
      if (isObjectEmpty(raw_radius_data)) {
        raw_radius_data[current_collection].forEach((value) => {
          let find_search = 0;
          let temp_row = value;
          if (current_collection === "authentication") {
            if (typeof value?.authdate === "number") {
              let authdate =
                new Date(value?.authdate * 1000).toLocaleDateString() +
                " " +
                new Date(value?.authdate * 1000).toLocaleTimeString();
              temp_row["authdate"] = authdate;
            }
          }
          if (current_collection === "accounting") {
            let current_epoch = Date.now();
            if (typeof value?.acctsessiontime === "number") {
              temp_row["acctsessiontime"] = secondsToDhms(
                value?.acctsessiontime
              );
            }
            if (
              typeof value?.acctstoptime === "number" ||
              !value?.acctstoptime
            ) {
              let acctstoptime = value?.acctstoptime
                ? new Date(value?.acctstoptime * 1000).toLocaleDateString() +
                  " " +
                  new Date(value?.acctstoptime * 1000).toLocaleTimeString()
                : 0;
              if (
                acctstoptime === 0 &&
                current_epoch - value?.acctupdatetime * 1000 > 1800000
              ) {
                acctstoptime =
                  "redcell" +
                  new Date(
                    value?.acctupdatetime * 1000 + 1800000
                  ).toLocaleDateString() +
                  " " +
                  new Date(
                    value?.acctupdatetime * 1000 + 1800000
                  ).toLocaleTimeString();
              }
              temp_row["acctstoptime"] = acctstoptime;
            }
            if (typeof value?.acctupdatetime === "number") {
              let acctupdatetime =
                new Date(value?.acctupdatetime * 1000).toLocaleDateString() +
                " " +
                new Date(value?.acctupdatetime * 1000).toLocaleTimeString();
              temp_row["acctupdatetime"] = acctupdatetime;
            }
            if (typeof value?.acctstarttime === "number") {
              let acctstarttime =
                new Date(value?.acctstarttime * 1000).toLocaleDateString() +
                " " +
                new Date(value?.acctstarttime * 1000).toLocaleTimeString();
              temp_row["acctstarttime"] = acctstarttime;
            }
          }
          if (search_value) {
            let row_values_array = Object.values(temp_row).join().split(", ");
            find_search = row_values_array.findIndex((element) =>
              element.includes(search_value)
            );
          }
          if (find_search >= 0) {
            updated_radius_data.push(temp_row);
          }
        });
      }
      setRadiusData(updated_radius_data);
    },
    []
  );

  const updateSelectList = useCallback(
    (select_array) => {
      setSelectList(select_array);
    },
    []
  );

  useEffect(() => {
    if (rawRadiusData) {
      // Update the list of collections available in the select dropdown
      const select_array = Object.keys(rawRadiusData);
      updateSelectList(select_array);

      // Make sure the selected collection is valid
      var current_collection = select_array?.[0];
      if (collection) {
        if (select_array.indexOf(collection) < 0) {
          setCollection(current_collection);
        } else {
          current_collection = collection;
        }
      }

      // Show the list of RADIUS data for the current collection
      FormatRadiusData(rawRadiusData, current_collection, searchTerm);
    }
  }, [rawRadiusData, collection, searchTerm, loading]);

  // Set the collection if the select list changes
  useEffect(() => {
    if (selectList.length && !collection) {
      setCollection(selectList?.[0]);
    }
  }, [selectList]);

  // Keep track of the last search term and the next time we can search
  const lastSearchText = useRef("");
  const nextSearchTime = useRef(0);

  function refreshData() {
    // We are starting a search - set variables to avoid duplicate searches
    lastSearchText.current = searchTerm;
    nextSearchTime.current = Date.now() + 30000;

    const query_data = {
      token_data: { token_data: token },
      collection_search: {
        search_name: (searchDB ? searchTerm : ""),
      },
    };
    setLoading(true);
    RadiusAPI(query_data, "POST", "collections", "")
      .then(function (response) {
        if (response?.status === 200) {
          if (response?.data) {
            setRawRadiusData(response?.data?.[0] || {accounting: [], authentication: []});
          }
        }
      })
      .catch(function (err) {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (!token) {
      return;
    }

    // Search if the search term changed or if the next search time has passed
    if((searchDB && searchTerm !== lastSearchText.current) || nextSearchTime.current < Date.now()) {
      let searchTimer = setTimeout(() => {
        refreshData();
      }, 500);

      // Clear the timer if we get more input
      return () => {
        clearTimeout(searchTimer);
      };
    }
  }, [token, refreshLogs, searchDB, searchTerm]);

  if (radiusData) {
    return (
      <Grid2 container>
        <Grid2 size={6}>
          {selectList.length ? (
            <FormControl fullWidth sx={{ marginTop: "2em" }}>
              <InputLabel id="collection-select-label">Collection</InputLabel>
              <Select
                labelId="collection-select-label-id"
                id="collection-simple-select"
                value={collection ? collection : selectList?.[0]}
                label="Collection"
                onChange={handleCollectionChange}
              >
                {selectList.map((option, index) => {
                  return (
                    <MenuItem value={option} key={index}>
                      {option}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : null}
        </Grid2>
        <Grid2 size={6}>
          {filterOptions?.length && isObjectEmpty(radiusData) ? (
            <FormControl fullWidth sx={{ marginTop: "2em" }}>
              <InputLabel id="filter-select-label">Column Filters</InputLabel>
              <Select
                labelId="filter-select-label-id"
                id="filter-simple-select"
                multiple
                value={filters}
                label="Column Filters"
                onChange={handleFilterChange}
                input={
                  <OutlinedInput
                    id="select-multiple-chip"
                    label="Column Filters"
                  />
                }
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {filterOptions.map((filter, index) => (
                  <MenuItem
                    key={filter + index}
                    value={filter}
                    style={getStyles(filter, filters, theme)}
                  >
                    {filter}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
        </Grid2>
        <Grid2 size={6}>
          <Search
            handleSearchChange={handleSearchChange}
            searchTerm={searchTerm}
          />
        </Grid2>
        <Grid2 size={0.5}>
          <Tooltip title="Refresh Logs">
            <IconButton
              sx={{ mt: "10px" }}
              onClick={handleRefresh}
              disabled={loading}
            >
              {loading ? <CircularProgress className="refreshwhitelist" size={20} /> : <RefreshTwoToneIcon className={"refreshwhitelist"} />}
            </IconButton>
          </Tooltip>
        </Grid2>
        <Grid2 size={5}>
          <FormGroup>
            <Tooltip
              title="Search additional records in DB on username column"
              sx={{ mt: "10px" }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={searchDB}
                    onChange={handleCheckboxChange}
                    inputProps={{ "aria-label": "controlled" }}
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 22 } }}
                  />
                }
                label="Search additional records in DB on username column"
              />
            </Tooltip>
          </FormGroup>
        </Grid2>

        <Grid2 size={12}>
          {isObjectEmpty(radiusData) ? (
            <RadiusTable
              radiusData={radiusData}
              collection={collection}
              defaultFilters={defaultFilters}
              filters={filters}
              searchTerm={searchTerm}
            />
          ) : (radiusData && !loading) ? (
            <Box>No Radius Data</Box>
          ) : (
            <Box sx={{ marginTop: "2em" }}>
              <CircularProgress />
            </Box>
          )}
        </Grid2>
      </Grid2>
    );
  } else {
    return (
      <Box sx={{ textAlign: "center", pt: "20vh" }}>
        <CircularProgress />
      </Box>
    );
  }
}
